import { UnderlyingSelectEnum } from 'enums/underlying';
import { checkCfiValidity } from 'validators/cfi';
import { checkIsinValidity } from 'validators/isin';
import { checkLeiValidity } from 'validators/lei';
import * as Yup from 'yup';
import { keycloakEmailRegex } from '../../../utils/validation-utils';
import { LeiCategory } from '../../../constants';

export const FILE_SIZE = 100 * 1024 * 1024;

export const SUPPORTED_FORMATS = ['application/pdf', 'zip'];

const DECIMAL_18_5 = '^([0-9]{0,13})(\\.[0-9]{0,5})?$';
const INTEGER_18 = '^([0-9]{0,18})$';
const messageInvalidInteger = 'Please enter a positive number (precision is 18)';
const messageInvalidDecimal = 'Please enter a positive number (precision is 18 with scale 5)';
const emailInvalid = 'Please enter a valid email';

function validateDecimal185(value: number | undefined | null): boolean {
  return !value || !!String(value).match(DECIMAL_18_5);
}

function validateInteger18(value: number | undefined | null): boolean {
  return !value || !!String(value).match(INTEGER_18);
}

export const FinalTermsValidator = Yup.object({
  contact: Yup.object({
    email: Yup.string()
      //Combining formik and keycloak email regex.
      //Keycloak is more restrictive on certain characters (like à, ä...etc)
      //But formik adds some rules like a mandatory extension, meaning email@localhost is valid in keycloak but not in formik.
      .email(emailInvalid)
      .matches(keycloakEmailRegex, emailInvalid)
      .required('Required'),
    name: Yup.string().required('Required'),
    phone: Yup.string(),
  }).defined(),
  baseProspectus: Yup.object({
    fileNumber: Yup.string()
      .matches('^C-([0-9]{6}$)' as any, 'Id must be like C-XXXXXX')
      .required('Required'),
    dateOfApproval: Yup.date().required('Required'),
  }).defined(),
  finalTermsDetails: Yup.object({
    date: Yup.date().required('Required'),
    language: Yup.string().required('Required'),
    leiIssuers: Yup.array()
      .of(
        Yup.object({
          category: Yup.mixed().oneOf(Object.values(LeiCategory)).required(),
          lei: Yup.string().test('lei', 'Please enter a valid LEI', checkLeiValidity).defined('Please enter a valid LEI'),
        }).defined()
      )
      .defined(),
    leiGuarantors: Yup.array()
      .of(
        Yup.object({
          category: Yup.mixed().oneOf(Object.values(LeiCategory)).required(),
          lei: Yup.string().test('lei', 'Please enter a valid LEI', checkLeiValidity).defined('Please enter a valid LEI'),
        }).defined()
      )
      .defined(),
  }).defined(),
  securities: Yup.array()
    .of(
      Yup.object({
        fisn: Yup.string().required('Please enter a valid FISN').max(35, 'Please enter a valid FISN'),
        isin: Yup.string().test('isin', 'Please enter a valid ISIN', checkIsinValidity).defined('Please enter a valid ISIN'),
        cfiCode: Yup.string().test('cfi', 'Please enter a valid CFI', checkCfiValidity).defined('Please enter a valid CFI'),
        bailInAbility: Yup.bool(),
        underlying: Yup.object({
          identifier: Yup.string(),
          nameOrIsin: Yup.string().test('testNameOrIsin', 'Please enter a valid ISIN', function () {
            // https://github.com/jquense/yup/pull/556
            return !this.parent.select || this.parent.select !== UnderlyingSelectEnum.isin || checkIsinValidity(this.parent.nameOrIsin);
          }),
        }),
        type: Yup.string().required('Required'),
        offerEndDate: Yup.date().when('offerToPublic', {
          is: true,
          then: (schema) => schema.required('Required'),
        }),
        maturityDate: Yup.date(),
        denominationPerUnit: Yup.number().test('is-decimal-18-5', messageInvalidDecimal, validateDecimal185),
        issuanceCurrency: Yup.string().required('Required'),
        volume: Yup.object({
          from: Yup.number().test('is-integer-18', messageInvalidInteger, validateInteger18),
          to: Yup.number().test('is-integer-18', messageInvalidInteger, validateInteger18),
          equal: Yup.number().test('is-integer-18', messageInvalidInteger, validateInteger18),
        }),
        price: Yup.object({
          from: Yup.number().test('is-decimal-18-5', messageInvalidDecimal, validateDecimal185),
          to: Yup.number().test('is-decimal-18-5', messageInvalidDecimal, validateDecimal185),
          equal: Yup.number().test('is-decimal-18-5', messageInvalidDecimal, validateDecimal185),
          pending: Yup.bool(),
        }),
        consideration: Yup.object({
          from: Yup.number().test('is-decimal-18-5', messageInvalidDecimal, validateDecimal185),
          to: Yup.number().test('is-decimal-18-5', messageInvalidDecimal, validateDecimal185),
          equal: Yup.number().test('is-decimal-18-5', messageInvalidDecimal, validateDecimal185),
          pending: Yup.bool(),
        }),
        typeOfOfferAdmission: Yup.string().required('Required'),
        offerToPublic: Yup.bool(),
        memberStateAdmissionToTrading: Yup.array().when('typeOfOfferAdmission', {
          is: (val: string) => ['IRMT', 'IPTM', 'IMTF', 'SOOA'].includes(val),
          then: (schema) => schema.required('Required'),
        }),
        memberStateOfferToPublic: Yup.array().when('offerToPublic', {
          is: true,
          then: (schema) => schema.required('Required'),
        }),
        tradingVenueCharacteristics: Yup.string().when('typeOfOfferAdmission', {
          is: (val: string) => ['IRMT', 'IPTM', 'IMTF', 'SIOP', 'SIWO', 'SOOA', 'IORM'].includes(val),
          then: (schema) => schema.required('Required'),
        }),
      }).defined()
    )
    .defined(),
  file: Yup.mixed()
    .required('A file is required')
    .test('fileSize', 'File too large', (value: any) => value && value.size <= FILE_SIZE)
    .test('fileFormat', 'Unsupported Format', (value: any) => value && SUPPORTED_FORMATS.includes(value.type)),
});
